/**
 * Sleep Styles Section Interactive Component
 * Handles desktop and mobile interactive functionality for pillow selection
 */
(function() {
  'use strict';

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Find all sleep styles sections on the page
    const sections = document.querySelectorAll('[data-sleep-styles-section]');

    sections.forEach(section => {
      const sectionId = section.dataset.sleepStylesSection;
      initializeSection(sectionId);
    });
  }

  function initializeSection(sectionId) {
    // Preload all images first
    preloadImages(sectionId);

    // Desktop interactive buttons
    initializeDesktopButtons(sectionId);

    // Mobile interactive functionality
    initializeMobileCarousel(sectionId);
  }

  /**
   * Preload all images for instant display
   */
  function preloadImages(sectionId) {
    const allButtons = document.querySelectorAll('.sleep-styles-' + sectionId + ' [data-key]');
    const imageUrls = new Set();

    // Collect all unique image URLs
    allButtons.forEach(button => {
      const imageUrl = button.getAttribute('data-key');
      if (imageUrl) {
        imageUrls.add(imageUrl);
      }
    });

    // Preload each image
    imageUrls.forEach(url => {
      const img = new Image();
      img.src = url;
    });
  }

  /**
   * Initialize desktop button functionality
   */
  function initializeDesktopButtons(sectionId) {
    const desktopButtons = document.querySelectorAll('.sleep-styles-' + sectionId + ' .tumble-btn-tooltip');
    const demoImage = document.getElementById('demo-image');

    if (!desktopButtons.length || !demoImage) return;

    desktopButtons.forEach(button => {
      button.addEventListener('click', function() {
        // Remove active state from all buttons
        desktopButtons.forEach(btn => {
          btn.classList.remove('sleep-button--active');
          btn.classList.add('sleep-button--inactive');
        });

        // Add active state to clicked button
        this.classList.remove('sleep-button--inactive');
        this.classList.add('sleep-button--active');

        // Update image with smooth transition
        const imageUrl = this.getAttribute('data-key');
        updateImage(demoImage, imageUrl);
      });
    });
  }

  /**
   * Initialize mobile carousel functionality
   */
  function initializeMobileCarousel(sectionId) {
    const mobileDemoImage = document.getElementById('mobile-demo-image');
    const mobileButtons = document.querySelectorAll('.sleep-styles-' + sectionId + ' .mobile-sleep-button');
    const carousel = document.getElementById('mobile-carousel-' + sectionId);
    const dots = document.querySelectorAll('.sleep-styles-' + sectionId + ' .carousel-dot');

    if (!carousel || !mobileDemoImage || !mobileButtons.length) return;

    // Initialize mobile buttons
    initializeMobileButtons(mobileButtons, mobileDemoImage);

    // Initialize carousel swipe
    initializeCarouselSwipe(carousel, dots, mobileDemoImage);

    // Initialize dot navigation
    initializeDotNavigation(dots, carousel, mobileDemoImage);
  }

  /**
   * Initialize mobile button clicks
   */
  function initializeMobileButtons(mobileButtons, mobileDemoImage) {
    mobileButtons.forEach(button => {
      // Prevent touch events on buttons from triggering carousel swipe
      button.addEventListener('touchstart', function(e) {
        e.stopPropagation();
      }, { passive: true });

      button.addEventListener('touchmove', function(e) {
        e.stopPropagation();
      }, { passive: true });

      button.addEventListener('touchend', function(e) {
        e.stopPropagation();
      }, { passive: true });

      // Handle button click
      button.addEventListener('click', function(e) {
        e.stopPropagation();
        e.preventDefault();

        const imageUrl = this.getAttribute('data-key');

        // Remove active state from sibling buttons in current slide only
        const siblings = this.closest('.mobile-sleep-buttons').querySelectorAll('.mobile-sleep-button');
        siblings.forEach(btn => {
          btn.classList.remove('sleep-button--active');
          btn.classList.add('sleep-button--inactive');
        });

        // Add active state to clicked button
        this.classList.remove('sleep-button--inactive');
        this.classList.add('sleep-button--active');

        // Update the shared mobile demo image
        updateImage(mobileDemoImage, imageUrl);
      });
    });
  }

  /**
   * Initialize carousel swipe functionality
   */
  function initializeCarouselSwipe(carousel, dots, mobileDemoImage) {
    let currentIndex = 0;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    carousel.addEventListener('touchstart', function(e) {
      startX = e.touches[0].clientX;
      isDragging = true;
      carousel.style.transition = 'none';
    });

    carousel.addEventListener('touchmove', function(e) {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
      const diff = currentX - startX;
      const offset = -currentIndex * 100 + (diff / carousel.offsetWidth) * 100;
      carousel.style.transform = `translateX(${offset}%)`;
    });

    carousel.addEventListener('touchend', function(e) {
      if (!isDragging) return;
      isDragging = false;

      const diff = currentX - startX;
      const threshold = carousel.offsetWidth * 0.2;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && currentIndex > 0) {
          currentIndex--;
        } else if (diff < 0 && currentIndex < 2) {
          currentIndex++;
        }
      }

      updateCarousel(carousel, dots, mobileDemoImage, currentIndex);
    });
  }

  /**
   * Initialize dot navigation
   */
  function initializeDotNavigation(dots, carousel, mobileDemoImage) {
    dots.forEach(dot => {
      dot.addEventListener('click', function() {
        const index = parseInt(this.getAttribute('data-index'));
        updateCarousel(carousel, dots, mobileDemoImage, index);
      });
    });
  }

  /**
   * Update carousel position and image
   */
  function updateCarousel(carousel, dots, mobileDemoImage, index) {
    const currentIndex = Math.max(0, Math.min(index, 2));
    const offset = -currentIndex * 100;

    carousel.style.transition = 'transform 0.3s ease-out';
    carousel.style.transform = `translateX(${offset}%)`;

    // Update dots
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === currentIndex);
    });

    // Update the shared image to show the first active button's image in the current slide
    const slides = carousel.querySelectorAll('.mobile-carousel-slide');
    const currentSlide = slides[currentIndex];

    if (currentSlide) {
      const firstActiveButton = currentSlide.querySelector('.mobile-sleep-button.sleep-button--active');
      if (firstActiveButton && mobileDemoImage) {
        const imageUrl = firstActiveButton.getAttribute('data-key');
        updateImage(mobileDemoImage, imageUrl);
      }
    }
  }

  /**
   * Update image with smooth fade transition
   */
  function updateImage(imageElement, imageUrl) {
    if (!imageElement || !imageUrl) return;

    imageElement.style.opacity = '0';

    setTimeout(() => {
      imageElement.style.backgroundImage = `url('${imageUrl}')`;

      setTimeout(() => {
        imageElement.style.opacity = '1';
      }, 50);
    }, 300);
  }
})();
